import { Interpolation, SpringValue } from 'react-spring'
import { AllowReadonly, DeepRequired } from '@feedyou/utils'
import { useMemo } from 'react'
import { FluidValue } from '@react-spring/shared'
import {
  addCustomMethodsToTimelinePrototype,
  ExtendPrototype
} from './addCustomMethodsToTimelinePrototype'
import { getStagesExtendedInfo } from './getStagesExtendedInfo'
import { getStageEnd, getStageStart, useMainTimeline } from './useMainTimeline'

type Easing = (inputNormalizedFrom0to1: number) => number

export type StageDefinition = {
  name: string
  duration?: number
  easing?: Easing
  stageOverflow?: { left?: number; right?: number }
  extrapolate?: { left?: boolean; right?: boolean }
}

type StageNames<S extends AllowReadonly<StageDefinition[]>> = S[number]['name']
type StageTimelinesById<S extends AllowReadonly<StageDefinition[]>> = Record<
  StageNames<S>,
  StageTimeline
>

export type ExtendedStageInfo = DeepRequired<StageDefinition> & { begin: number; end: number }

export type MainTimeline = SpringValue<number> // A central timer that actives and de-activates stages, as it progresses

type StageTimelineBase = Interpolation<number, number> & FluidValue
export type StageTimeline = ExtendPrototype<StageTimelineBase> & FluidValue

// Tip: use `as const` when constructing schema to get autocomplete for timeline names
export const useAnimationStageTimelines = <S extends AllowReadonly<StageDefinition[]>>(
  isExpanding: boolean,
  schema: S
): StageTimelinesById<S> => {
  const extendedStagesInfo = useMemo(() => getStagesExtendedInfo(schema), [schema])

  const mainTimeline = useMainTimeline(extendedStagesInfo, isExpanding) // mainTimeline ref stabilized internally by useSpring

  const stageTimelines = useMemo(
    () => createStageTimelines(extendedStagesInfo, mainTimeline),
    [mainTimeline, extendedStagesInfo]
  )

  addCustomMethodsToTimelinePrototype(mainTimeline)
  return stageTimelines as unknown as StageTimelinesById<S>
}

const createStageTimelines = <S extends ExtendedStageInfo[]>(
  extendedStagesInfo: S,
  mainTimeline: MainTimeline
) => {
  return extendedStagesInfo.reduce((timers, stageInfo) => {
    const stageName: StageNames<S> = stageInfo.name

    timers[stageName] = createStageTimeline(stageInfo, mainTimeline)

    return timers
  }, {} as Record<StageNames<S>, StageTimelineBase>)
}

const createStageTimeline = (stageInfo: ExtendedStageInfo, mainTimeline: MainTimeline) => {
  const newStageTimelineLinear = subscribeNewStageToMainTimeline(mainTimeline, stageInfo)
  const newStageWithEasing = applyEasing(newStageTimelineLinear, stageInfo.easing)

  return newStageWithEasing
}

const subscribeNewStageToMainTimeline = (
  mainTimeline: MainTimeline,
  stageInfo: ExtendedStageInfo
): StageTimelineBase => {
  return mainTimeline.to({
    range: [getStageStart(stageInfo), getStageEnd(stageInfo)],
    output: [0, 1],
    extrapolateLeft: stageInfo.extrapolate.left ? 'extend' : 'clamp',
    extrapolateRight: stageInfo.extrapolate.right ? 'extend' : 'clamp'
  })
}

// Careful: timeline's interpolation output should map to the range [0,1]
const applyEasing = (timeline: StageTimelineBase, easing: Easing) => {
  return timeline.to(easing)
}
